import type { CompanyStatus, SubscriptionType } from "./enums";
import { err, ok, type Result } from "./result";
import { parseHttpUrl } from "./url";

/** Company billing status values written by Stripe webhook handlers. */
export type MappedSubscriptionStatus = Extract<
  CompanyStatus,
  "active" | "pastDue" | "canceled"
>;

export type BillingReturnStatus = "success" | "cancelled";

declare const billingOriginBrand: unique symbol;

export type BillingOrigin = string & { readonly [billingOriginBrand]: "BillingOrigin" };

export type BillingOriginError =
  | { readonly kind: "missingOrigin" }
  | { readonly kind: "invalidOrigin"; readonly reason: string }
  | { readonly kind: "unsupportedOriginProtocol"; readonly protocol: string }
  | { readonly kind: "nonOriginUrl" };

export const billingOriginErrorMessage = (error: BillingOriginError): string => {
  switch (error.kind) {
    case "missingOrigin":
      return "origin is required";
    case "invalidOrigin":
      return `origin is not a valid URL: ${error.reason}`;
    case "unsupportedOriginProtocol":
      return "origin must be an http:// or https:// URL";
    case "nonOriginUrl":
      return "origin must not include a path, query string, or hash";
  }
};

export const parseBillingOrigin = (origin: string): Result<BillingOrigin, BillingOriginError> => {
  const value = origin.trim();
  if (value === "") return err({ kind: "missingOrigin" });

  const parsed = parseHttpUrl(value);
  if (!parsed.ok) {
    switch (parsed.error.kind) {
      case "missingUrl":
        return err({ kind: "missingOrigin" });
      case "invalidUrl":
        return err({ kind: "invalidOrigin", reason: parsed.error.reason });
      case "unsupportedProtocol":
        return err({ kind: "unsupportedOriginProtocol", protocol: parsed.error.protocol });
    }
  }

  const { url } = parsed.value;
  if (url.pathname !== "/" || url.search !== "" || url.hash !== "") {
    return err({ kind: "nonOriginUrl" });
  }

  return ok(url.origin as BillingOrigin);
};

export type BillingReturn = {
  readonly status: BillingReturnStatus;
  readonly sessionId: string | null;
};

export type BillingWorkflowState<PlanId extends string = string> = {
  readonly checkoutPlanId: PlanId | null;
  readonly premiumCheckoutBusy: boolean;
  readonly portalBusy: boolean;
  readonly billingError: string | null;
  readonly checkoutNotice: BillingReturnStatus | null;
  readonly billingSyncing: boolean;
  readonly billingReturn: BillingReturn | null;
};

export type BillingWorkflowAction<PlanId extends string = string> =
  | { readonly type: "billingReturnDetected"; readonly value: BillingReturn }
  | { readonly type: "billingReturnParseFailed"; readonly error: string }
  | { readonly type: "billingReturnNoticeShown"; readonly status: BillingReturnStatus }
  | { readonly type: "billingReturnCleared" }
  | { readonly type: "billingSyncStarted" }
  | { readonly type: "billingSyncSucceeded"; readonly clearReturn: boolean }
  | { readonly type: "billingSyncFailed"; readonly error: string; readonly clearReturn: boolean }
  | { readonly type: "checkoutStarted"; readonly planId: PlanId }
  | { readonly type: "checkoutFailed"; readonly error: string }
  | { readonly type: "premiumCheckoutUnavailable"; readonly message: string }
  | { readonly type: "premiumCheckoutStarted" }
  | { readonly type: "premiumCheckoutFailed"; readonly error: string }
  | { readonly type: "portalStarted" }
  | { readonly type: "portalFailed"; readonly error: string };

export const emptyBillingWorkflowState = <PlanId extends string = string>(): BillingWorkflowState<PlanId> => ({
  checkoutPlanId: null,
  premiumCheckoutBusy: false,
  portalBusy: false,
  billingError: null,
  checkoutNotice: null,
  billingSyncing: false,
  billingReturn: null,
});

export const nextBillingWorkflowState = <PlanId extends string>(
  state: BillingWorkflowState<PlanId>,
  action: BillingWorkflowAction<PlanId>,
): BillingWorkflowState<PlanId> => {
  switch (action.type) {
    case "billingReturnDetected":
      return { ...state, billingReturn: action.value };
    case "billingReturnParseFailed":
      return { ...state, billingError: action.error, billingReturn: null };
    case "billingReturnNoticeShown":
      return { ...state, checkoutNotice: action.status };
    case "billingReturnCleared":
      return { ...state, billingReturn: null };
    case "billingSyncStarted":
      return { ...state, billingSyncing: true, billingError: null };
    case "billingSyncSucceeded":
      return {
        ...state,
        billingSyncing: false,
        billingReturn: action.clearReturn ? null : state.billingReturn,
      };
    case "billingSyncFailed":
      return {
        ...state,
        billingSyncing: false,
        billingError: action.error,
        billingReturn: action.clearReturn ? null : state.billingReturn,
      };
    case "checkoutStarted":
      return { ...state, billingError: null, checkoutPlanId: action.planId };
    case "checkoutFailed":
      return { ...state, billingError: action.error, checkoutPlanId: null };
    case "premiumCheckoutUnavailable":
      return { ...state, billingError: action.message };
    case "premiumCheckoutStarted":
      return { ...state, billingError: null, premiumCheckoutBusy: true };
    case "premiumCheckoutFailed":
      return { ...state, billingError: action.error, premiumCheckoutBusy: false };
    case "portalStarted":
      return { ...state, billingError: null, portalBusy: true };
    case "portalFailed":
      return { ...state, billingError: action.error, portalBusy: false };
  }
};

export type BillingReturnParams = {
  readonly billing: string | null;
  readonly sessionId: string | null;
};

export type BillingReturnParamsError = {
  readonly kind: "invalidBillingReturnStatus";
  readonly value: string;
};

export type BillingPlanMatch<PlanId extends string = string> =
  | { readonly kind: "notRequested" }
  | { readonly kind: "notFound" }
  | { readonly kind: "matched"; readonly planId: PlanId };

export type BillingStatusPatchInput<PlanId extends string = string> = {
  readonly status: MappedSubscriptionStatus;
  readonly currentActivePlanId: PlanId | null;
  readonly planMatch: BillingPlanMatch<PlanId>;
};

export type BillingStatusPatch<PlanId extends string = string> = {
  readonly status: MappedSubscriptionStatus;
  readonly activePlanId: PlanId | null;
};

export type BillingPlanForDisplay<PlanId extends string = string> = {
  readonly _id: PlanId;
  readonly tier: SubscriptionType;
  readonly stripePriceId: string | null;
  readonly priceAudCents: number;
  readonly interval: "month" | "year";
};

export type CurrentPlanDisplayPlan = {
  readonly name: string;
  readonly tier: SubscriptionType;
};

export type CurrentPlanDisplayCompany = {
  readonly status: CompanyStatus;
  readonly trialEndsAt: number | null;
};

export type CurrentPlanDisplayState =
  | { readonly kind: "plan"; readonly name: string; readonly tier: SubscriptionType }
  | { readonly kind: "trial"; readonly daysRemaining: number | null }
  | { readonly kind: "none" };

export const currentPlanDisplayState = ({
  plan,
  company,
  now,
}: {
  readonly plan: CurrentPlanDisplayPlan | null;
  readonly company: CurrentPlanDisplayCompany;
  readonly now: number;
}): CurrentPlanDisplayState => {
  if (plan !== null) return { kind: "plan", name: plan.name, tier: plan.tier };
  if (hasActiveTrial(company, now)) {
    return { kind: "trial", daysRemaining: activeTrialDaysRemaining(company, now) };
  }
  return { kind: "none" };
};

export const billingReturnParamsErrorMessage = (error: BillingReturnParamsError): string => {
  switch (error.kind) {
    case "invalidBillingReturnStatus":
      return `Unexpected billing return status: ${error.value}`;
  }
};

const billingReturnStatus = (value: string): BillingReturnStatus | null =>
  value === "success" || value === "cancelled" ? value : null;

export const parseBillingReturnParams = (
  params: BillingReturnParams,
): Result<BillingReturn | null, BillingReturnParamsError> => {
  const billing = params.billing?.trim() ?? "";
  if (billing === "") return ok(null);

  const status = billingReturnStatus(billing);
  if (status === null) return err({ kind: "invalidBillingReturnStatus", value: billing });

  const sessionId = params.sessionId?.trim() ?? "";
  return ok({ status, sessionId: sessionId === "" ? null : sessionId });
};

export const formatPlanPrice = (
  cents: number,
  interval: "month" | "year",
): string =>
  `A$${(cents / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })} / ${interval}`;

export const trialDaysRemaining = (trialEndsAt: number, now: number): number =>
  Math.max(0, Math.ceil((trialEndsAt - now) / (24 * 60 * 60 * 1000)));

export const hasActiveTrial = (
  company: { readonly status: CompanyStatus; readonly trialEndsAt: number | null },
  now: number,
): boolean => company.status === "trialing" && !trialExpired(company, now);

export const activeTrialDaysRemaining = (
  company: { readonly status: CompanyStatus; readonly trialEndsAt: number | null },
  now: number,
): number | null => {
  if (!hasActiveTrial(company, now) || company.trialEndsAt === null) return null;
  return trialDaysRemaining(company.trialEndsAt, now);
};

export type TrialSetupBannerState =
  | { readonly kind: "activeTrial"; readonly label: string; readonly daysRemaining: number | null }
  | { readonly kind: "trialFirst"; readonly label: "Trial first"; readonly daysRemaining: null };

export const trialSetupBannerState = (
  company: { readonly status: CompanyStatus; readonly trialEndsAt: number | null },
  now: number,
): TrialSetupBannerState => {
  const daysRemaining = activeTrialDaysRemaining(company, now);
  if (hasActiveTrial(company, now)) {
    return {
      kind: "activeTrial",
      label: daysRemaining === null
        ? "Free trial active"
        : `Free trial active · ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} remaining`,
      daysRemaining,
    };
  }
  return { kind: "trialFirst", label: "Trial first", daysRemaining: null };
};

export type BillingNoticeView =
  | { readonly kind: "none" }
  | { readonly kind: "success"; readonly tone: "success"; readonly message: string }
  | { readonly kind: "cancelled"; readonly tone: "neutral"; readonly message: string }
  | { readonly kind: "syncing"; readonly tone: "info"; readonly message: string };

export const billingNoticeView = (
  checkoutNotice: BillingReturnStatus | null,
  billingSyncing: boolean,
): BillingNoticeView => {
  switch (checkoutNotice) {
    case "success":
      return {
        kind: "success",
        tone: "success",
        message: billingSyncing
          ? "Payment received — finalizing your plan in Lawstream..."
          : "Payment received — your plan is now active.",
      };
    case "cancelled":
      return { kind: "cancelled", tone: "neutral", message: "Checkout cancelled." };
    case null:
      return billingSyncing
        ? { kind: "syncing", tone: "info", message: "Checking Stripe billing status..." }
        : { kind: "none" };
  }
};

export type VisibleBillingNoticeView = Exclude<BillingNoticeView, { readonly kind: "none" }>;

const BILLING_NOTICE_TONE_CLASSES = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  cancelled: "border-slate-200 bg-slate-50 text-slate-600",
  syncing: "border-sky-200 bg-sky-50 text-sky-800",
} satisfies Record<VisibleBillingNoticeView["kind"], string>;

export const billingNoticeToneClass = (view: VisibleBillingNoticeView): string =>
  BILLING_NOTICE_TONE_CLASSES[view.kind];

export type BillingPlanCardView =
  | {
      readonly kind: "current";
      readonly disabled: true;
      readonly highlighted: true;
      readonly interactive: false;
      readonly busy: false;
      readonly cursor: "default";
      readonly actionLabel: "Current plan";
      readonly actionTone: "current";
    }
  | {
      readonly kind: "redirecting";
      readonly disabled: true;
      readonly highlighted: false;
      readonly interactive: false;
      readonly busy: true;
      readonly cursor: "pointer";
      readonly actionLabel: "Redirecting...";
      readonly actionTone: "muted";
    }
  | {
      readonly kind: "blocked";
      readonly disabled: true;
      readonly highlighted: false;
      readonly interactive: false;
      readonly busy: false;
      readonly cursor: "pointer";
      readonly actionLabel: "Select plan";
      readonly actionTone: "primary";
    }
  | {
      readonly kind: "selectable";
      readonly disabled: false;
      readonly highlighted: false;
      readonly interactive: true;
      readonly busy: false;
      readonly cursor: "pointer";
      readonly actionLabel: "Select plan";
      readonly actionTone: "primary";
    };

export const billingPlanCardView = <PlanId extends string>({
  planId,
  activePlanId,
  checkoutPlanId,
  checkoutBusy,
}: {
  readonly planId: PlanId;
  readonly activePlanId: PlanId | null;
  readonly checkoutPlanId: PlanId | null;
  readonly checkoutBusy: boolean;
}): BillingPlanCardView => {
  if (activePlanId === planId) {
    return {
      kind: "current",
      disabled: true,
      highlighted: true,
      interactive: false,
      busy: false,
      cursor: "default",
      actionLabel: "Current plan",
      actionTone: "current",
    };
  }
  if (checkoutPlanId === planId) {
    return {
      kind: "redirecting",
      disabled: true,
      highlighted: false,
      interactive: false,
      busy: true,
      cursor: "pointer",
      actionLabel: "Redirecting...",
      actionTone: "muted",
    };
  }
  if (checkoutBusy) {
    return {
      kind: "blocked",
      disabled: true,
      highlighted: false,
      interactive: false,
      busy: false,
      cursor: "pointer",
      actionLabel: "Select plan",
      actionTone: "primary",
    };
  }
  return {
    kind: "selectable",
    disabled: false,
    highlighted: false,
    interactive: true,
    busy: false,
    cursor: "pointer",
    actionLabel: "Select plan",
    actionTone: "primary",
  };
};

export type PremiumCheckoutCommand<PlanId extends string = string> =
  | { readonly kind: "ready"; readonly planId: PlanId }
  | { readonly kind: "unavailable"; readonly message: string };

export type BillingActionButtonView = {
  readonly disabled: boolean;
  readonly label: string;
};

export const billingPortalButtonView = (portalBusy: boolean): BillingActionButtonView => ({
  disabled: portalBusy,
  label: portalBusy ? "Opening..." : "Manage billing",
});

export const premiumCheckoutButtonView = ({
  checkoutBusy,
  premiumCheckoutBusy,
}: {
  readonly checkoutBusy: boolean;
  readonly premiumCheckoutBusy: boolean;
}): BillingActionButtonView => ({
  disabled: checkoutBusy,
  label: premiumCheckoutBusy ? "Redirecting..." : "View Enterprise",
});

export const sortPlansByPrice = <Plan extends { readonly priceAudCents: number }>(
  plans: readonly Plan[],
): Plan[] => [...plans].sort((a, b) => a.priceAudCents - b.priceAudCents);

export const activeBillingPlan = <Plan extends { readonly _id: string }>(
  plans: readonly Plan[],
  activePlanId: Plan["_id"] | null,
): Plan | null =>
  activePlanId === null
    ? null
    : plans.find((plan) => plan._id === activePlanId) ?? null;

export const premiumCheckoutPlan = <Plan extends BillingPlanForDisplay>(
  plans: readonly Plan[],
): Plan | null =>
  plans.find((plan) => plan.tier === "premium" && plan.stripePriceId !== null) ?? null;

export const premiumCheckoutCommand = <Plan extends BillingPlanForDisplay>(
  plan: Plan | null,
): PremiumCheckoutCommand<Plan["_id"]> =>
  plan === null
    ? { kind: "unavailable", message: "No premium plan is available." }
    : { kind: "ready", planId: plan._id };

export const visibleBillingPlans = <Plan extends BillingPlanForDisplay>(
  plans: readonly Plan[],
  activePlanId: Plan["_id"] | null,
): Plan[] =>
  plans.filter((plan) => plan.stripePriceId !== null || plan._id === activePlanId);

export type StripeMetadata = Readonly<Record<string, string | undefined>> | null | undefined;
export type StripeMetadataField = "companyId" | "planId";

export type StripeMetadataError =
  | { readonly kind: "missingMetadataField"; readonly field: StripeMetadataField }
  | { readonly kind: "blankMetadataField"; readonly field: StripeMetadataField };

export type CheckoutMetadata = {
  readonly companyId: string;
  readonly planId: string;
};

export type SubscriptionMetadata = {
  readonly companyId: string;
};

export const stripeMetadataErrorMessage = (error: StripeMetadataError): string => {
  switch (error.kind) {
    case "missingMetadataField":
      return `missing ${error.field} in metadata`;
    case "blankMetadataField":
      return `blank ${error.field} in metadata`;
  }
};

const metadataField = (
  metadata: StripeMetadata,
  field: StripeMetadataField,
): Result<string, StripeMetadataError> => {
  const value = metadata?.[field];
  if (value === undefined) return err({ kind: "missingMetadataField", field });

  const cleaned = value.trim();
  return cleaned === "" ? err({ kind: "blankMetadataField", field }) : ok(cleaned);
};

export const parseCheckoutMetadata = (
  metadata: StripeMetadata,
): Result<CheckoutMetadata, StripeMetadataError> => {
  const companyId = metadataField(metadata, "companyId");
  if (!companyId.ok) return companyId;

  const planId = metadataField(metadata, "planId");
  if (!planId.ok) return planId;

  return ok({ companyId: companyId.value, planId: planId.value });
};

export const parseSubscriptionMetadata = (
  metadata: StripeMetadata,
): Result<SubscriptionMetadata, StripeMetadataError> => {
  const companyId = metadataField(metadata, "companyId");
  return companyId.ok ? ok({ companyId: companyId.value }) : companyId;
};
/** Map a Stripe subscription status to the company's billing status. */
export const mapStripeSubscriptionStatus = (
  status: string,
): MappedSubscriptionStatus => {
  switch (status) {
    case "active":
    case "trialing":
      return "active";
    case "past_due":
    case "unpaid":
      return "pastDue";
    default:
      return "canceled";
  }
};

const activePlanIdFromMatch = <PlanId extends string>(
  currentActivePlanId: PlanId | null,
  planMatch: BillingPlanMatch<PlanId>,
): PlanId | null => {
  switch (planMatch.kind) {
    case "matched":
      return planMatch.planId;
    case "notFound":
    case "notRequested":
      return currentActivePlanId;
  }
};

export const billingStatusPatch = <PlanId extends string>(
  input: BillingStatusPatchInput<PlanId>,
): BillingStatusPatch<PlanId> => ({
  status: input.status,
  activePlanId: input.status === "canceled"
    ? null
    : activePlanIdFromMatch(input.currentActivePlanId, input.planMatch),
});

/** Whether a trialing company's trial window has ended. */
export const trialExpired = (
  company: { status: CompanyStatus; trialEndsAt: number | null },
  now: number,
): boolean =>
  company.status === "trialing" &&
  company.trialEndsAt !== null &&
  company.trialEndsAt < now;

/**
 * Whether a company may self-serve add/change legislation subscriptions.
 * Requires an active plan or an unexpired trial.
 */
export const canSelfServeSubscribe = (
  company: {
    status: CompanyStatus;
    trialEndsAt: number | null;
    activePlanId: string | null;
  },
  now: number,
): boolean => {
  if (company.activePlanId !== null) return true;
  if (company.status === "trialing" && !trialExpired(company, now)) return true;
  return false;
};
